import React, { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  buildItemPriceTimeline,
  buildTimelineChartScale,
  formatTimelineAxisTick,
  formatTimelineMoney,
} from '../utils/itemPriceTimeline.mjs';

const formatDate = (value, includeYear = true) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  });
};

const PriceTooltip = ({ active, payload }) => {
  const event = active && payload?.[0]?.payload;
  if (!event) return null;
  return (
    <div style={{ background: '#fff', border: '1px solid #DCCFC4', borderRadius: 10, padding: '0.7rem 0.8rem', boxShadow: '0 8px 24px rgba(58,46,40,0.14)', minWidth: 170 }}>
      <div style={{ color: '#8A7468', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{formatDate(event.timestamp)}</div>
      <div style={{ color: '#3A2E28', fontWeight: 800, marginTop: '0.2rem' }}>{event.title}</div>
      <div style={{ color: '#3A2E28', fontSize: '0.9rem', marginTop: '0.15rem' }}>{event.description}</div>
      <div style={{ color: '#6F6259', fontSize: '0.75rem', marginTop: '0.35rem' }}>{event.source}{event.vendor ? ` • ${event.vendor}` : ''}</div>
    </div>
  );
};

export default function ItemPriceTimeline({ item, historyRows, multiVendor = false }) {
  const timeline = useMemo(
    () => buildItemPriceTimeline(item, historyRows, null, { multiVendor }),
    [item, historyRows, multiVendor],
  );
  const { basis, events } = timeline;
  const [activeIndex, setActiveIndex] = useState(Math.max(0, events.length - 1));

  useEffect(() => {
    setActiveIndex(Math.max(0, events.length - 1));
  }, [item?.id, events.length]);

  const chartWidth = Math.max(720, events.length * 190);
  const chartScale = useMemo(() => buildTimelineChartScale(events), [events]);
  const { data: chartData, domain: dateDomain, ticks: dateTicks, spanMs: dateSpanMs } = chartScale;
  const latestPricedEvent = [...events].reverse().find((event) => event.price !== null);
  const currentPrice = latestPricedEvent?.title === 'Price removed' ? null : latestPricedEvent?.price;
  const hasPricedEvents = events.some((event) => event.price !== null);
  const updateCount = events.filter((event) => event.type === 'price_update').length;
  const activeEvent = events[activeIndex] || events.at(-1);
  const basisLabel = basis === 'case' ? 'Case price' : 'Unit price';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.65rem', marginBottom: '1rem' }}>
        <div style={{ background: '#F8F4EF', border: '1px solid #E8DDD4', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ color: '#8A7468', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Current {basis} price</div>
          <div style={{ color: '#3A2E28', fontSize: '1.35rem', fontWeight: 800, marginTop: '0.1rem' }}>{formatTimelineMoney(currentPrice)}</div>
        </div>
        <div style={{ background: '#F8F4EF', border: '1px solid #E8DDD4', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ color: '#8A7468', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Added to catalog</div>
          <div style={{ color: '#3A2E28', fontSize: '1rem', fontWeight: 800, marginTop: '0.25rem' }}>{formatDate(events[0]?.timestamp)}</div>
        </div>
        <div style={{ background: '#F8F4EF', border: '1px solid #E8DDD4', borderRadius: 10, padding: '0.75rem 0.85rem' }}>
          <div style={{ color: '#8A7468', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recorded price events</div>
          <div style={{ color: '#3A2E28', fontSize: '1rem', fontWeight: 800, marginTop: '0.25rem' }}>{updateCount} update{updateCount === 1 ? '' : 's'}</div>
        </div>
      </div>

      <div style={{ color: '#6F6259', fontSize: '0.78rem', marginBottom: '0.45rem' }}>
        Scroll horizontally to follow the item over time. Hover or select an event for details.
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid #E8DDD4', borderRadius: 12, background: '#FFFEFC', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ width: chartWidth, minWidth: '100%', paddingTop: '0.65rem' }}>
          {hasPricedEvents ? (
            <div style={{ width: '100%', height: 245 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 18, right: 28, left: 8, bottom: 18 }}
                  onMouseMove={(state) => {
                    if (Number.isInteger(state?.activeTooltipIndex)) setActiveIndex(state.activeTooltipIndex);
                  }}
                >
                  <CartesianGrid stroke="#EEE5DE" strokeDasharray="3 4" vertical={false} />
                  <XAxis
                    type="number"
                    dataKey="time"
                    scale="time"
                    domain={dateDomain}
                    ticks={dateTicks}
                    tickFormatter={(value) => formatTimelineAxisTick(value, dateSpanMs)}
                    tick={{ fill: '#7B6A61', fontSize: 12 }}
                    axisLine={{ stroke: '#D9CBC0' }}
                    tickLine={false}
                    allowDataOverflow
                  />
                  <YAxis
                    tickFormatter={(value) => `$${Number(value).toFixed(0)}`}
                    tick={{ fill: '#7B6A61', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={58}
                    domain={[(min) => Math.max(0, min * 0.92), (max) => max === 0 ? 1 : max * 1.08]}
                  />
                  <Tooltip content={<PriceTooltip />} />
                  <Line
                    type="stepAfter"
                    dataKey="price"
                    name={basisLabel}
                    stroke="#6F8F5B"
                    strokeWidth={3}
                    connectNulls
                    dot={{ r: 5, fill: '#fff', stroke: '#4F713C', strokeWidth: 3 }}
                    activeDot={{ r: 7, fill: '#4F713C', stroke: '#fff', strokeWidth: 3 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 245, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7B6A61', fontSize: '0.9rem' }}>
              This item does not have a recorded price yet.
            </div>
          )}

          <div style={{ borderTop: '1px solid #E8DDD4', padding: '0 28px 0 66px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${events.length}, minmax(170px, 1fr))` }}>
              {events.map((event, index) => {
                const selected = index === activeIndex;
                return (
                  <button
                    key={event.id}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    style={{
                      textAlign: 'left', border: 'none', borderRight: index < events.length - 1 ? '1px solid #E8DDD4' : 'none',
                      background: selected ? '#EEF4EA' : '#FFFEFC', padding: '0.8rem 0.85rem', cursor: 'pointer', minHeight: 122,
                      boxShadow: selected ? 'inset 0 3px 0 #5E7F49' : 'inset 0 3px 0 transparent',
                    }}
                    aria-pressed={selected}
                  >
                    <div style={{ color: '#8A7468', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.035em' }}>{formatDate(event.timestamp)}</div>
                    <div style={{ color: '#3A2E28', fontSize: '0.9rem', fontWeight: 800, marginTop: '0.22rem' }}>{event.title}</div>
                    <div style={{ color: '#3A2E28', fontSize: '0.82rem', marginTop: '0.18rem' }}>{event.description}</div>
                    <div style={{ color: '#74665E', fontSize: '0.72rem', marginTop: '0.35rem' }}>{event.source}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {activeEvent && (
        <div aria-live="polite" style={{ marginTop: '0.75rem', padding: '0.7rem 0.85rem', borderLeft: '4px solid #6F8F5B', background: '#F5F8F2', color: '#4E433D', fontSize: '0.82rem' }}>
          <strong>{activeEvent.title}</strong> on {formatDate(activeEvent.timestamp)} — {activeEvent.description}.
          {activeEvent.vendor ? ` Vendor: ${activeEvent.vendor}.` : ''} Source: {activeEvent.source}.
        </div>
      )}
    </div>
  );
}
